<template>
  <modal :show="show" :width="1200" @close="closeModal">
    <v-flex slot="title">Usługi kontenera</v-flex>

    <div>
      <material-table
        entity-type="CONTAINER_BENEFIT"
        :headers="table.headers"
        :actions="actions"
        :items="containerBenefitList"
        :total-count="containerBenefitCount"
        @edit="setInputs"
        @delete="remove($event.id)"
      />

      <v-layout v-if="!showAddInputs">
        <v-flex text-xs-right>
          <check-permission permissions="CONTAINER_BENEFIT.ADD" entity-type="partner" :entity-id="partnerId">
            <v-btn color="success" @click="showAddInputs = true">Dodaj usługę do kontenera</v-btn>
          </check-permission>
        </v-flex>
      </v-layout>

      <div v-if="showAddInputs">
        <v-layout>
          <v-flex xs4 mx-2>
            <v-autocomplete
              v-model="selected"
              clearable
              hide-no-data
              cache-items
              item-text="name"
              item-value="id"
              return-object
              placeholder="Wyszukaj usługę"
              :disabled="!!idToEdit"
              :items="list"
              :search-input.sync="search"
            >
              <div slot="label">Usługi</div>
            </v-autocomplete>
          </v-flex>

          <v-flex xs4 mx-2>
            <w-schema :model="$schema.points">
              <v-text-field label="Liczba punktów przyznawanych za użycie usługi*" />
            </w-schema>
          </v-flex>

          <v-flex xs4 mx-2>
            <w-schema :model="$schema.maxUsagesPerUser">
              <v-text-field label="Maksymalna liczba naliczeń punktów" />
            </w-schema>
          </v-flex>
        </v-layout>

        <span class="caption grey--text">*oznaczone pola są wymagane</span>

        <v-layout>
          <v-flex xs12 text-xs-right>
            <v-btn color="error" flat @click="resetForm">Anuluj</v-btn>

            <check-permission
              entity-type="partner"
              :permissions="'CONTAINER_BENEFIT.' + (idToEdit ? 'UPDATE' : 'ADD')"
              :entity-id="partnerId"
            >
              <v-btn color="success" @click="save">Zapisz</v-btn>
            </check-permission>
          </v-flex>
        </v-layout>
      </div>
    </div>

    <v-layout slot="actions">
      <v-spacer />

      <v-btn flat @click="closeModal">Zamknij</v-btn>
    </v-layout>
  </modal>
</template>

<script>
import { formatError } from '@witkac/kartamiejska-shared/dist/filters/formatError';
import { tryParseInt } from '@witkac/utils/tryParseInt';
import { WSchema } from '@witkac/vuetify-components';
import { mapState } from 'vuex';

import Modal from '@/components/material/Modal';
import MaterialTable from '@/components/material/Table';
import { manageMixin } from '@/mixins/manage';
import schema from '@/schemas/containerBenefitAssigment.schema';
import { DELETE_ACTION, EDIT_ACTION } from '@/utils/actions';

const emptyData = () => ({
  benefitId: null,
  points: null,
  maxUsagesPerUser: null
});
const messages = {
  listError: 'Wystąpił błąd podczas pobierania listy przypisań usług.',
  getError: 'Wystąpił błąd podczas pobierania danych przypisania usługi.',
  added: 'Dodano przypisanie usługi.',
  addError: 'Nie udało się dodać przypisania usługi.',
  updated: 'Zaktualizowano dane przypisania usługi.',
  updateError: 'Nie udało się zaktualizować danych przypisania usługi.',
  deleted: 'Usunięto przypisanie usługi.',
  deleteError: 'Nie udało się usunąć przypisania usługi.'
};

// @vuese
// Modal przypisań usług do kontenera
// @group Partners
export default {
  components: { Modal, MaterialTable, WSchema },
  mixins: [manageMixin('partners/pointContainers', messages, emptyData)],
  props: {
    // Identyfikator kontenera na punkty
    pointContainerId: { type: Number, required: false },
    // Czy zarządzanie globalnymi przypisaniami
    global: { type: Boolean }
  },
  data() {
    return {
      showAddInputs: false,
      selected: undefined,
      idToEdit: null,
      search: '',
      table: {
        headers: [
          {
            value: 'benefitName',
            text: 'Nazwa usługi',
            sortable: false,
            render: item => item.benefit && item.benefit.name
          },
          {
            value: 'pointContainerName',
            text: 'Nazwa kontenera',
            sortable: false,
            render: item => item.pointContainer && item.pointContainer.name
          },
          { value: 'points', text: 'Punkty za użycie', sortable: false },
          { value: 'maxUsagesPerUser', text: 'Maksymalna liczba użyć', sortable: false }
        ]
      }
    };
  },
  schema,
  computed: {
    ...mapState({
      partnerId: state => state.partners.id,
      stateData: state => state.partners.pointContainers.data,
      list: state => state.partners.benefits.list,
      containerBenefitList: function(state) {
        return this.global ? state.containerBenefit.list : state.partners.containerBenefit.list;
      },
      containerBenefitCount: function(state) {
        return this.global ? state.containerBenefit.count : state.partners.containerBenefit.count;
      }
    }),

    actions() {
      return [
        { ...DELETE_ACTION, entityType: 'partner', entityId: this.partnerId },
        { ...EDIT_ACTION, entityType: 'partner', entityId: this.partnerId }
      ];
    }
  },
  watch: {
    search: {
      immediate: true,
      handler(value) {
        this.searchData(value);
      }
    },
    selected(value) {
      if (value && value.id) {
        this.select();
      }
    },
    show(value) {
      if (value) {
        this.setData();
        this.setContainer();
      }
    }
  },
  methods: {
    // @vuese
    // Ustawienie pola w schemie
    setContainer() {
      this.$schema.pointContainerId.$setData(this.pointContainerId);
    },

    // @vuese
    // Wyszukanie benefitu
    searchData(search) {
      const filters = { search, count: 5 };
      filters.partnerId = tryParseInt(this.$route.params.id);
      this.$store.dispatch('partners/benefits/list', filters);
    },

    // @vuese
    // Wybranie benefitu
    select() {
      if (this.selected) {
        this.$schema.benefitId.$setData(this.selected.id);
      }
    },
    // @vuese
    // Zapisanie przypisania benefitu do usługi
    save() {
      if (this.$schema.$validateAll().length) {
        return this.notifyError('Należy poprawić błędy w formularzu');
      }

      return this.$store
        .dispatch(`partners/containerBenefit/${this.idToEdit ? 'update' : 'add'}`, {
          containerBenefit: this.$schema.$getData(),
          id: this.idToEdit
        })
        .then(() =>
          this.$store.dispatch('partners/containerBenefit/list', { count: -1, pointContainerId: this.pointContainerId })
        )
        .then(() => {
          this.notifySuccess(messages.added);
          this.resetForm();
        })
        .catch(err => this.notifyError(formatError(err)));
    },
    // @vuese
    // Reset formularza
    resetForm() {
      this.selected = undefined;
      this.$schema.$setData(emptyData());
      this.showAddInputs = false;
      this.idToEdit = null;
    },
    // @vuese
    // Usuwanie przypisania
    remove(id) {
      this.$store
        .dispatch('partners/containerBenefit/remove', id)
        .then(() => this.notifySuccess(messages.deleted))
        .catch(err => this.notifyError(messages.deleteError));
    },
    // @vuese
    // Ustawienie inputów edycji
    setInputs(containerBenefit) {
      this.selected = containerBenefit.benefit;
      this.search = containerBenefit.benefit && containerBenefit.benefit.name;
      this.$schema.benefitId.$setData(containerBenefit.benefit.id);
      this.$schema.points.$setData(containerBenefit.points);
      this.$schema.maxUsagesPerUser.$setData(containerBenefit.maxUsagesPerUser);
      this.idToEdit = containerBenefit.id;

      this.showAddInputs = true;
    }
  }
};
</script>
